import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
} from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@radix-ui/react-tabs";
import { Plus, ListTree } from "lucide-react";
import {
  CarouselItemStatus,
  type ICarouselItem,
  type ICarouselOrderedItem,
} from "../../../types/models/carousel.models.types";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { useGetCarouselItemsAdminHttp, useGetCarouselOrderAdminHttp } from "../../../hooks/http/admin/carousel/get-carousel-items.admin.http";
import {
  useArchiveCarouselItemAdminHttp,
  usePublishCarouselItemAdminHttp,
  useUpdateCarouselOrderAdminHttp,
} from "../../../hooks/http/admin/carousel/carousel-mutations.admin.http";
import CarouselItemCard from "../../../components/carousel-item-card.components";
import CarouselListItemCard from "../../../components/carousel-list-item-card.components";
import { SortableList } from "../../../components/sortable-list/sortable-list.components";
import Spinner from "../../../components/spinner.components";
import PaginationComponent from "../../../components/pagination.components";
import { Button } from "../../../components/ui/button";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";

const tabs: { label: string; status: CarouselItemStatus }[] = [
  { label: "Live", status: CarouselItemStatus.LIVE },
  { label: "Draft", status: CarouselItemStatus.DRAFT },
  { label: "Archived", status: CarouselItemStatus.ARCHIVED },
];

const CarouselsAdminScreen: FunctionComponent = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<CarouselItemStatus>(
    CarouselItemStatus.LIVE,
  );
  const [items, setItems] = useState<ICarouselItem[] | ICarouselOrderedItem[]>([]);
  const [pagination, setPagination] = useState<IPagination | undefined>();
  const [page, setPage] = useState(1);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  
  // Reordering state
  const [isReordering, setIsReordering] = useState(false);
  const [orderedItems, setOrderedItems] = useState<ICarouselOrderedItem[]>([]);

  const { execute: getCarouselItems, fetching: fetchingItems } =
    useGetCarouselItemsAdminHttp();
  const { execute: getCarouselOrder, fetching: fetchingOrder } = 
    useGetCarouselOrderAdminHttp();
  const { execute: updateCarouselOrder, fetching: updatingOrder } = 
    useUpdateCarouselOrderAdminHttp();
  const { execute: publishItem } = usePublishCarouselItemAdminHttp();
  const { execute: archiveItem } = useArchiveCarouselItemAdminHttp();

  const fetching = fetchingItems || fetchingOrder;

  const fetchItems = useCallback(
    async (status: CarouselItemStatus, pg: number) => {
      setIsReordering(false);
      if (status === CarouselItemStatus.LIVE) {
        const response = await getCarouselOrder({ body: null });
        if (!response.success) return handleHttpError(response, toast);
        setItems(response.data.carousel);
        setOrderedItems(response.data.carousel);
        setPagination(undefined);
      } else {
        const response = await getCarouselItems({
          body: null,
          queryParams: { status, page: pg },
        });
        if (!response.success) return handleHttpError(response, toast);
        setItems(response.data.carouselItems);
        setPagination(response.data.pagination);
      }
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
    [toast],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    setItems([]);
    setPage(1);
    fetchItems(activeTab, 1);
  }, [activeTab]);

  async function handlePublish(id: string) {
    setPublishingId(id);
    const response = await publishItem({
      body: null,
      pathParams: { id },
    });
    setPublishingId(null);
    if (!response.success) return handleHttpError(response, toast);
    toast({ content: "Carousel item is now live!", toastType: "success" });
    fetchItems(activeTab, page);
  }

  async function handleArchive(id: string) {
    setArchivingId(id);
    const response = await archiveItem({
      body: null,
      pathParams: { id },
    });
    setArchivingId(null);
    if (!response.success) return handleHttpError(response, toast);
    toast({ content: "Carousel item archived.", toastType: "success" });
    fetchItems(activeTab, page);
  }

  async function handleSaveOrder() {
    const newOrderPayload = orderedItems.map((item, index) => ({
      id: item.id,
      order: index + 1,
    }));
    const response = await updateCarouselOrder({
      body: { carousel: newOrderPayload },
    });
    if (!response.success) return handleHttpError(response, toast);
    
    toast({ content: "Carousel order updated successfully!", toastType: "success" });
    setIsReordering(false);
    setItems(response.data.carousel);
    setOrderedItems(response.data.carousel);
  }
  const isOrderChanged = useMemo(() => {
    return items.map(i => i.id).join(',') !== orderedItems.map(i => i.id).join(',');
  }, [items, orderedItems]);

  return (
    <div className="flex flex-col flex-1 gap-8 p-8">
      <div className="flex flex-col gap-2 px-5">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-2xl">Carousels</span>
          <Button
            onClick={() => navigate("/admin/carousels/new")}
            className="flex items-center gap-2 bg-primarybuttonbg px-8 py-6 border border-border rounded-lg font-semibold text-base text-primarybuttontext tracking-wider"
          >
            <Plus className="w-4 h-4" /> New carousel item
          </Button>
        </div>

        <div className="py-4">
          <div className="flex items-center justify-between mb-8 border-b border-border">
            {isReordering ? (
              <div className="flex items-center px-5 py-2.5 w-full">
                <span className="font-semibold text-base text-primarytext">Change order</span>
              </div>
            ) : (
              <Tabs
                value={activeTab}
                onValueChange={(val) => setActiveTab(val as CarouselItemStatus)}
                className="w-full"
              >
                <TabsList className="flex w-fit items-center gap-0">
                  {tabs.map((tab) => (
                    <TabsTrigger
                      key={tab.status}
                      value={tab.status}
                      className={`px-5 py-2.5 font-semibold text-sm border-b-2 transition-colors ${
                        activeTab === tab.status
                          ? "border-primary text-primary"
                          : "border-transparent text-secondary"
                      }`}
                    >
                      {tab.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            )}
            
            {activeTab === CarouselItemStatus.LIVE && items.length > 0 && (
              <div className="flex items-center gap-3 pb-2 whitespace-nowrap">
                {isReordering ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsReordering(false);
                        setOrderedItems(items as ICarouselOrderedItem[]);
                      }}
                      className="border-border text-secondary font-semibold"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveOrder}
                      disabled={updatingOrder || !isOrderChanged}
                      className="bg-primarybuttonbg text-primarybuttontext font-semibold border-border border w-32 flex justify-center items-center"
                    >
                      {updatingOrder ? <Spinner width={16} /> : "Save Order"}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="default"
                    onClick={() => setIsReordering(true)}
                    className="flex items-center gap-2 bg-tertiarybuttonbg border border-border text-tertiarybuttontext font-semibold"
                  >
                    <ListTree className="w-4 h-4" /> Change Order
                  </Button>
                )}
              </div>
            )}
          </div>

          <Tabs value={activeTab}>
            {tabs.map((tab) => (
              <TabsContent key={tab.status} value={tab.status}>
                {items.length === 0 && fetching ? (
                  <div className="flex justify-center items-center py-24">
                    <Spinner />
                  </div>
                ) : items.length === 0 ? (
                  <div className="flex justify-center items-center py-24">
                    <p className="text-secondary text-sm">
                      No {tab.label.toLowerCase()} carousel items
                    </p>
                  </div>
                ) : (
                  <div>
                    {isReordering && activeTab === CarouselItemStatus.LIVE ? (
                      <div className="w-full  mx-auto">
                        <SortableList
                          items={orderedItems}
                          onChange={setOrderedItems}
                          keyExtractor={(item) => item.id}
                          renderItem={(item, index) => (
                            <CarouselListItemCard item={item} order={index + 1} />
                          )}
                        />
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-6 w-full">
                        {items.map((item) => (
                          <CarouselItemCard
                            key={item.id}
                            item={item as ICarouselItem}
                            onPublish={handlePublish}
                            onArchive={handleArchive}
                            publishing={publishingId === item.id}
                            archiving={archivingId === item.id}
                          />
                        ))}
                      </div>
                    )}
                    
                    {!isReordering && pagination && (
                      <PaginationComponent
                        fetching={fetching}
                        paginationData={pagination}
                        onClick={() => {
                          const nextPage = page + 1;
                          setPage(nextPage);
                          fetchItems(activeTab, nextPage);
                        }}
                      />
                    )}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CarouselsAdminScreen;

